import React from 'react'
import { PageSettings } from '@http/api-models/page-settings'
import { PageLayoutOwnProps } from '@shared/components/page-layout/model'
import { NextPageFC } from '@shared/models/next-fc'
import { GetServerSideProps } from 'next'
import { MainPageV2 } from 'screens/main-page-v2'

import { AdminRootPage } from '../../admin/components/RootPage'
import { AdminNotFoundPage } from '../../admin/errors'
import { getPageConfigByUrl, getPageText } from '../../admin/http'
import { getHeadPropsFromPageMeta } from '../../admin/utils'
import { regionalPages } from '../../regions/regional-pages'
import {
	getDynamicRegionalPage,
	getPageParamOrDefault,
} from '../../regions/runtime-components/helpers'
import { RegionalPagesPath, regionalPagesProps } from '../../regions/runtime-components/pages-props'
import { RuntimeComponentProps } from '../../regions/runtime-components/types'
import { serverSideResolveCity } from '../../ssr-resolvers/next-ssr-resolvers'
import { getSavingsParams } from '../../ssr-resolvers/savings-params'

const Index: NextPageFC<
	RuntimeComponentProps & { pageSettings: PageSettings },
	PageLayoutOwnProps
> = ({ regionalPagePath, pageSettings, ...rest }) => {
	const Component = React.useMemo(() => {
		const RegionalPage = getDynamicRegionalPage(regionalPagePath!)
		return RegionalPage ? RegionalPage.DynamicComponent : (props) => MainPageV2(props)
	}, [regionalPagePath])

	/**
	 * Интеграция админки
	 */
	if (pageSettings?.admin?.page) {
		return <AdminRootPage {...pageSettings} />
	}

	return <Component {...(rest as any)} />
}

Index.breadcrumb = { title: 'Кредиты' }

Index.layout = (props) => {
	return getPageParamOrDefault(
		props as any,
		'layout',
		regionalPagesProps[RegionalPagesPath.root].layout
	)
}

Index.layoutProps = {
	layoytVersion: 'version-2',
}

Index.headProps = (props) => {
	/**
	 * Интеграция админки
	 */
	if (props.pageSettings?.admin?.page) {
		return getHeadPropsFromPageMeta(props.pageSettings?.admin?.page.pageMeta)
	}

	return getPageParamOrDefault(
		props as any,
		'headProps',
		regionalPagesProps[RegionalPagesPath.root].headProps
	)
}

export const getServerSideProps: GetServerSideProps<
	{ pageSettings?: PageSettings } & RuntimeComponentProps
> = async (context) => {
	const { city: cityOrPathname } = context.params!

	const props: { pageSettings: PageSettings } & RuntimeComponentProps = {
		city: cityOrPathname as string,
		pageSettings: {},
	}

	try {
		props.pageSettings.savings = {
			params: await getSavingsParams(),
		}
	} catch {
		// silence
	}

	/**
	 * Интеграция админки
	 */
	try {
		const adminPageConfig = await getPageConfigByUrl(`/${cityOrPathname}`)

		if (adminPageConfig) {
			if (!adminPageConfig.isPublished) {
				throw new AdminNotFoundPage()
			}

			props.pageSettings.admin = {
				...props.pageSettings.admin,
				page: adminPageConfig,
			}

			return {
				props,
			}
		}
	} catch (err) {
		// silence
	}

	if (regionalPages.includes(cityOrPathname!.toString())) {
		props.regionalPagePath = cityOrPathname!.toString()
		return {
			props,
		}
	}

	return serverSideResolveCity(context)
}

export default Index
